import { cloneDeep } from 'lodash';
import findResult, {
  hasKey,
  isEmpty,
  removeEmpty,
  resultDiff,
} from '@/utils/resultsHelper';
import { checkRequired, checkMaxLength } from '@/utils/errors';
import fieldNames, { globalFieldLengths } from '@/constants/fields';
import formFieldTypes from '@/forms/constants';
import { formFieldErrors } from '@/forms/helper';
import { transformForm } from '@/forms/state/state';
import { readStates } from '@/constants/lifecycle';
import { blankCatalogueTemplate } from './templates/state/state';
import { disableDatasetEdit } from '../status/helper';
import { datasetSettingsFields } from '../settings/state/state';
import { datasourceFields } from '../datasource/state/state';

export const resolveCatalogueTemplate = (
  datasets,
  templates,
  { category, code },
  datasetIfNoExists = undefined,
  features = null,
) => {
  const dataset = findResult(datasets, category, code) || findResult(datasets, category, code, 'previousCode');
  const settings = dataset ? dataset.settings : datasetSettingsFields();
  const datasourceFeatures = dataset ? findResult(features, category, dataset.code) : null;
  const templateCategory = dataset?.templateCategory || category;
  const template = findResult(templates, templateCategory, settings.catalogue_definition_code)
        || blankCatalogueTemplate();
  const exists = !isEmpty(dataset);

  return {
    dataset: dataset || datasetIfNoExists,
    datasourceFeatures: datasourceFeatures || { ...datasourceFields(), ...readStates },
    exists,
    template,
    editable: exists && !disableDatasetEdit(dataset),
  };
};

const catalogueDescriptionField = (intl) => ({
  name: fieldNames.DESCRIPTION.toLowerCase(),
  type: formFieldTypes.MD.type,
  label: intl.formatMessage({ id: 'dataset.catalogue.description.title' }),
  validation: { maximum: globalFieldLengths.description },
  system: true,
});

export const catalogueTitleField = (intl) => ({
  name: 'title',
  type: formFieldTypes.STRING.type,
  placeholder: intl.formatMessage({ id: 'dataset.catalogue.name.placeholder' }),
  validation: { maximum: globalFieldLengths.name, required: true },
  system: true,
});

export const addFieldsToSections = ({ sections }, { intl }) => {
  const copy = cloneDeep(sections);
  const [name] = Object.keys(sections);
  copy[name].fields.unshift(catalogueDescriptionField(intl));

  return copy;
};

export const catalogueTitleErrors = ({ catalogue }) => {
  const errors = [];
  if (hasKey(catalogue, 'title')) {
    checkRequired(
      catalogue.title,
      [fieldNames.NAME],
      errors,
      'catalogue',
      'save.dataset.validation.required.name.title',
      true,
      'save.dataset.validation.required.name',
    );
    checkMaxLength(
      catalogue.title,
      [fieldNames.NAME],
      errors,
      'catalogue',
      'save.dataset.validation.name.title',
      [globalFieldLengths.name],
    );
  }

  return errors;
};

export const validateCatalogueSection = (catalogue, template, section) => (
  formFieldErrors(catalogue, `catalogue.invalid.section.${section}`, template.sections[section].fields, section)
);

export const validateCatalogue = (catalogue, template) => {
  const errors = catalogueTitleErrors({ catalogue });

  const fieldErrors = Object.keys(template.sections).map((section) => (
    validateCatalogueSection(catalogue, template, section)).flat());

  return errors.concat(...fieldErrors);
};

export const catalogueDiff = (current, previous) => resultDiff(transformForm(current), previous);

export const sectionsWithFields = ({ sections }) => removeEmpty(Object.keys(sections).map((name) => (
  sections[name].fields.length === 0 ? null : name
)));
